/**
 * File: apps/qbench-cp/reactapp/src/Dashboard/state.js
 *
 * Purpose: Provides the Dashboard-scoped state store (orders, filters, invoice filter) via
 *     React context.
 *
 * Scope: Dashboard-level order list, sample, test, and invoice filtering state within the
 *     customer portal.
 *
 * Tags: customer-portal
 *
 * Overview: Combines the `reducer` and `filters` reducers into a single root reducer applied
 *     over an initialState covering the orders list, order/sample/test filters, search
 *     conditions, and invoice filter/search state. Wraps useReducer in a thunk-supporting
 *     dispatch (useReducerWithThunk) so action creators can be plain functions that receive
 *     dispatch and state, in addition to plain action objects. Exposes the resulting state and
 *     dispatch through DashboardContext.
 *
 * Dependencies: ./reducers/reducer.js, ./reducers/filters.js, shared/constants/statuses.js.
 *
 * Exports: useDashboardStore — hook returning { state, dispatch } from DashboardContext.
 *     DashboardProvider — context provider component. DashboardContext — the underlying
 *     React context (exported for direct consumption where needed). initialState — the
 *     starting state shape.
 */

import React, { useReducer, createContext, useContext } from 'react';
import reducer from './reducers/reducer.js';
import filters from './reducers/filters.js';
import { ORDERS_STATE } from 'shared/constants/statuses.js';

function useReducerWithThunk(reducer, initialState) {
  const [state, dispatch] = useReducer(reducer, initialState);
  let customDispatch = React.useCallback(
    (action) => {
      if (typeof action === 'function') {
        action(customDispatch, state);
      } else {
        dispatch(action);
      }
    },
    [dispatch, state],
  );
  return [state, customDispatch];
}

const combineReducers =
  (...reducers) =>
  (state = initialState, action) => {
    for (let i = 0; i < reducers.length; i++)
      state = reducers[i](state, action);
    return state;
  };

export const initialState = {
  orders: [],
  filters: {
    orders: {
      statuses: [],
      date: {
        start: '',
        end: '',
      },
      custom: [],
    },
    samples: {
      accessioningTypes: [],
      sourceIds: [],
      searchById: '',
      custom: [],
    },
    tests: {
      statuses: [],
      assays: [],
      custom: {},
    },
    conditions: [ORDERS_STATE[0].name],
    mainSearch: '',
  },
  invoiceFilter: {
    status: [],
    search: {
      searchText: '',
      betweenSearchText: '',
      searchLogic: '',
    },
    orderId: [],
  },
};

const rootReducer = combineReducers(reducer, filters);

export const DashboardContext = createContext(initialState);
export const useDashboardStore = () => useContext(DashboardContext);

export const DashboardProvider = ({ children }) => {
  const [state, dispatch] = useReducerWithThunk(rootReducer, initialState);

  const store = React.useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <DashboardContext.Provider
      value={{ state: store.state, dispatch: store.dispatch }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
